/**
 * ButtonGroupExamples.tsx
 * Comprehensive examples of HeroUI ButtonGroup component
 * 
 * Showcases all HeroUI ButtonGroup features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { ButtonGroup } from '@enfomentis/ui';
import { Card, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { 
  BarChart3, 
  LineChart, 
  PieChart,
  TrendingUp,
  TrendingDown,
  Grid3X3,
  List,
  Table,
  Eye,
  EyeOff,
  Play,
  Pause,
  SkipForward,
  Volume1,
  Volume2,
  VolumeX,
  Sun,
  Moon,
  Laptop,
  DollarSign,
  Euro,
  PoundSterling
} from 'lucide-react';


export const ButtonGroupExamples: React.FC = () => {
  // Single selection states - each ButtonGroup gets its own independent state
  const [selectedChart, setSelectedChart] = useState('bar');
  const [selectedChart2, setSelectedChart2] = useState('bar');
  const [selectedView, setSelectedView] = useState('grid');
  const [selectedTimeframe, setSelectedTimeframe] = useState('1d');
  
  // Theme selection states for different variant demos
  const [selectedThemeSolid, setSelectedThemeSolid] = useState('light');
  const [selectedThemeBordered, setSelectedThemeBordered] = useState('light');
  const [selectedThemeFlat, setSelectedThemeFlat] = useState('light');
  const [selectedThemeGhost, setSelectedThemeGhost] = useState('light');
  
  // Currency selection states for different size demos
  const [selectedCurrencySm, setSelectedCurrencySm] = useState('usd');
  const [selectedCurrencyMd, setSelectedCurrencyMd] = useState('usd');
  const [selectedCurrencyLg, setSelectedCurrencyLg] = useState('usd');
  
  // Other interactive states
  const [selectedVolume, setSelectedVolume] = useState('medium');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedNav, setSelectedNav] = useState('dashboard');
  const [selectedOrderType, setSelectedOrderType] = useState('market');
  const [selectedDirection, setSelectedDirection] = useState('buy');
  const [selectedTIF, setSelectedTIF] = useState('gtc');
  const [selectedPortfolioView, setSelectedPortfolioView] = useState('positions');
  const [selectedMediaControl, setSelectedMediaControl] = useState('pause');
  const [selectedVisibility, setSelectedVisibility] = useState('show');

  // Multiple selection states  
  const [selectedFilters, setSelectedFilters] = useState(['profit']);
  const [selectedTools, setSelectedTools] = useState(['charts', 'analysis']);

  // Chart type options
  const chartOptions = [
    { label: 'Bar Chart', value: 'bar', icon: BarChart3 },
    { label: 'Line Chart', value: 'line', icon: LineChart },
    { label: 'Pie Chart', value: 'pie', icon: PieChart }
  ];

  // View options
  const viewOptions = [
    { label: 'Grid', value: 'grid', icon: Grid3X3 },
    { label: 'List', value: 'list', icon: List },
    { label: 'Table', value: 'table', icon: Table }
  ];

  // Timeframe options
  const timeframeOptions = [
    { label: '1D', value: '1d' },
    { label: '1W', value: '1w' },
    { label: '1M', value: '1m' },
    { label: '3M', value: '3m' },
    { label: '1Y', value: '1y' },
    { label: 'All', value: 'all' }
  ];

  // Theme options
  const themeOptions = [
    { label: 'Light', value: 'light', icon: Sun },
    { label: 'Dark', value: 'dark', icon: Moon },
    { label: 'Auto', value: 'auto', icon: Laptop }
  ];

  // Currency options
  const currencyOptions = [
    { label: 'USD', value: 'usd', icon: DollarSign },
    { label: 'EUR', value: 'eur', icon: Euro },
    { label: 'JPY', value: 'jpy', icon: PoundSterling },
    { label: 'GBP', value: 'gbp', icon: PoundSterling }
  ];

  // Volume options
  const volumeOptions = [
    { label: 'Mute', value: 'mute', icon: VolumeX },
    { label: 'Low', value: 'low', icon: Volume1 },
    { label: 'Medium', value: 'medium', icon: Volume2 }
  ];

  // Filter options for multiple selection
  const filterOptions = [
    { label: 'Profit', value: 'profit' },
    { label: 'Loss', value: 'loss' },
    { label: 'Pending', value: 'pending' },
    { label: 'Closed', value: 'closed' }
  ];

  // Tool options for multiple selection
  const toolOptions = [
    { label: 'Charts', value: 'charts' },
    { label: 'Analysis', value: 'analysis' },
    { label: 'Alerts', value: 'alerts' },
    { label: 'Reports', value: 'reports' }
  ];

  const handleMultipleSelection = (
    currentSelected: string[],
    setSelected: (selected: string[]) => void,
    value: string
  ) => {
    if (currentSelected.includes(value)) {
      setSelected(currentSelected.filter(item => item !== value));
    } else {
      setSelected([...currentSelected, value]);
    }
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Simple Button Group</p>
                  <ButtonGroup
                    options={[
                      { label: 'First', value: 'first' },
                      { label: 'Second', value: 'second' },
                      { label: 'Third', value: 'third' }
                    ]}
                    value="first"
                    onChange={(value) => console.log('Selected:', value)}
                  />
                </div>
                
                <div>
                  <p className="text-sm text-text-secondary mb-2">With Icons</p>
                  <ButtonGroup
                    options={chartOptions}
                    value={selectedChart2}
                    onChange={(value) => setSelectedChart2(String(value || 'bar'))}
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedChart2}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Single Selection</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Chart Type Selection</p>
                  <ButtonGroup
                    options={chartOptions}
                    value={selectedChart}
                    onChange={(value) => setSelectedChart(String(value || 'bar'))}
                    variant="flat"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedChart}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">View Mode Selection</p>
                  <ButtonGroup
                    options={viewOptions}
                    value={selectedView}
                    onChange={(value) => setSelectedView(String(value || 'grid'))}
                    variant="bordered"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedView}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Timeframe Selection</p>
                  <ButtonGroup
                    options={timeframeOptions}
                    value={selectedTimeframe}
                    onChange={(value) => setSelectedTimeframe(String(value || '1d'))}
                    variant="solid"
                    size="sm"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedTimeframe}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Different Variants</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Solid Variant</p>
                  <ButtonGroup
                    options={themeOptions}
                    value={selectedThemeSolid}
                    onChange={(value) => setSelectedThemeSolid(String(value || 'light'))}
                    variant="solid"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedThemeSolid}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Bordered Variant</p>
                  <ButtonGroup
                    options={themeOptions}
                    value={selectedThemeBordered}
                    onChange={(value) => setSelectedThemeBordered(String(value || 'light'))}
                    variant="bordered"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedThemeBordered}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Flat Variant</p>
                  <ButtonGroup
                    options={themeOptions}
                    value={selectedThemeFlat}
                    onChange={(value) => setSelectedThemeFlat(String(value || 'light'))}
                    variant="flat"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedThemeFlat}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Ghost Variant</p>
                  <ButtonGroup
                    options={themeOptions}
                    value={selectedThemeGhost}
                    onChange={(value) => setSelectedThemeGhost(String(value || 'light'))}
                    variant="ghost"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedThemeGhost}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Different Sizes</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Small Size</p>
                  <ButtonGroup
                    options={currencyOptions}
                    value={selectedCurrencySm}
                    onChange={(value) => setSelectedCurrencySm(String(value || 'usd'))}
                    size="sm"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedCurrencySm}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Medium Size (Default)</p>
                  <ButtonGroup
                    options={currencyOptions}
                    value={selectedCurrencyMd}
                    onChange={(value) => setSelectedCurrencyMd(String(value || 'usd'))}
                    size="md"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedCurrencyMd}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Large Size</p>
                  <ButtonGroup
                    options={currencyOptions}
                    value={selectedCurrencyLg}
                    onChange={(value) => setSelectedCurrencyLg(String(value || 'usd'))}
                    size="lg"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedCurrencyLg}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Multiple Selection</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Trade Filters (Multiple Selection)</p>
                  <div className="flex flex-wrap gap-2">
                    {filterOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleMultipleSelection(selectedFilters, setSelectedFilters, option.value)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-standard ${
                          selectedFilters.includes(option.value)
                            ? 'bg-primary text-text-inverse border-primary'
                            : 'bg-surface text-text-primary border-border hover:bg-hover'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-text-tertiary mt-1">
                    Selected: {selectedFilters.length > 0 ? selectedFilters.join(', ') : 'none'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Dashboard Tools (Multiple Selection)</p>
                  <div className="flex flex-wrap gap-2">
                    {toolOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleMultipleSelection(selectedTools, setSelectedTools, option.value)}
                        className={`px-4 py-2 text-sm rounded-lg border transition-standard ${
                          selectedTools.includes(option.value)
                            ? 'bg-success text-text-inverse border-success'
                            : 'bg-surface text-text-primary border-border hover:bg-hover'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-text-tertiary mt-1">
                    Selected: {selectedTools.length > 0 ? selectedTools.join(', ') : 'none'}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Full Width</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Full Width Button Group</p>
                  <ButtonGroup
                    options={[
                      { label: 'Overview', value: 'overview' },
                      { label: 'Trades', value: 'trades' },
                      { label: 'Analytics', value: 'analytics' }
                    ]}
                    value={selectedTab}
                    onChange={(value) => setSelectedTab(String(value || 'overview'))}
                    variant="solid"
                    className="w-full"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedTab}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Navigation Tabs</p>
                  <ButtonGroup
                    options={[
                      { label: 'Dashboard', value: 'dashboard' },
                      { label: 'Portfolio', value: 'portfolio' },
                      { label: 'Reports', value: 'reports' },
                      { label: 'Settings', value: 'settings' }
                    ]}
                    value={selectedNav}
                    onChange={(value) => setSelectedNav(String(value || 'dashboard'))}
                    variant="bordered"
                    className="w-full"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedNav}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Use Cases</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Order Type Selection</p>
                  <ButtonGroup
                    options={[
                      { label: 'Market', value: 'market' },
                      { label: 'Limit', value: 'limit' },
                      { label: 'Stop', value: 'stop' },
                      { label: 'Stop-Limit', value: 'stop-limit' }
                    ]}
                    value={selectedOrderType}
                    onChange={(value) => setSelectedOrderType(String(value || 'market'))}
                    variant="solid"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedOrderType}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Trade Direction</p>
                  <ButtonGroup
                    options={[
                      { label: 'Buy', value: 'buy', icon: TrendingUp },
                      { label: 'Sell', value: 'sell', icon: TrendingDown }
                    ]}
                    value={selectedDirection}
                    onChange={(value) => setSelectedDirection(String(value || 'buy'))}
                    variant="solid"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedDirection}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Time in Force</p>
                  <ButtonGroup
                    options={[
                      { label: 'GTC', value: 'gtc' },
                      { label: 'IOC', value: 'ioc' },
                      { label: 'FOK', value: 'fok' },
                      { label: 'Day', value: 'day' }
                    ]}
                    value={selectedTIF}
                    onChange={(value) => setSelectedTIF(String(value || 'gtc'))}
                    variant="bordered"
                    size="sm"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedTIF}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Portfolio View</p>
                  <ButtonGroup
                    options={[
                      { label: 'Positions', value: 'positions' },
                      { label: 'Orders', value: 'orders' },
                      { label: 'History', value: 'history' },
                      { label: 'P&L', value: 'pnl' }
                    ]}
                    value={selectedPortfolioView}
                    onChange={(value) => setSelectedPortfolioView(String(value || 'positions'))}
                    variant="flat"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedPortfolioView}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Interactive Controls</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Media Controls</p>
                  <ButtonGroup
                    options={[
                      { label: 'Play', value: 'play', icon: Play },
                      { label: 'Pause', value: 'pause', icon: Pause },
                      { label: 'Next', value: 'next', icon: SkipForward }
                    ]}
                    value={selectedMediaControl}
                    onChange={(value) => setSelectedMediaControl(String(value || 'pause'))}
                    variant="ghost"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedMediaControl}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Volume Control</p>
                  <ButtonGroup
                    options={volumeOptions}
                    value={selectedVolume}
                    onChange={(value) => setSelectedVolume(String(value || 'medium'))}
                    variant="flat"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedVolume}</p>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Visibility Toggle</p>
                  <ButtonGroup
                    options={[
                      { label: 'Show', value: 'show', icon: Eye },
                      { label: 'Hide', value: 'hide', icon: EyeOff }
                    ]}
                    value={selectedVisibility}
                    onChange={(value) => setSelectedVisibility(String(value || 'show'))}
                    variant="bordered"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Selected: {selectedVisibility}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Advanced Features</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <p className="text-sm text-text-secondary mb-2">Disabled Options</p>
                  <ButtonGroup
                    options={[
                      { label: 'Available', value: 'available' },
                      { label: 'Disabled', value: 'disabled', disabled: true },
                      { label: 'Active', value: 'active' },
                      { label: 'Unavailable', value: 'unavailable', disabled: true }
                    ]}
                    value="available"
                    onChange={(value) => console.log('Selection:', value)}
                    variant="solid"
                  />
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">With Badges</p>
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <Badge content="3" color="danger" size="sm">
                        <button className="px-4 py-2 bg-surface hover:bg-hover text-text-primary border border-border rounded-lg transition-standard">
                          Alerts
                        </button>
                      </Badge>
                      <Badge content="12" color="primary" size="sm">
                        <button className="px-4 py-2 bg-surface hover:bg-hover text-text-primary border border-border rounded-lg transition-standard">
                          Orders
                        </button>
                      </Badge>
                      <Badge content="new" color="success" size="sm">
                        <button className="px-4 py-2 bg-surface hover:bg-hover text-text-primary border border-border rounded-lg transition-standard">
                          Features
                        </button>
                      </Badge>
                    </div>
                    <p className="text-xs text-text-tertiary">Individual buttons with badges - not a ButtonGroup but shows composition possibilities</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-text-secondary mb-2">Custom Styling</p>
                  <ButtonGroup
                    options={[
                      { label: 'Profit', value: 'profit' },
                      { label: 'Break Even', value: 'break-even' },
                      { label: 'Loss', value: 'loss' }
                    ]}
                    value="profit"
                    onChange={(value) => console.log('P&L Filter:', value)}
                    variant="flat"
                    className="bg-surface-elevated"
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </PlaygroundPageLayout>
  );
};